// Vendors
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime, Duration } from 'luxon';

// Actions
import { openModal, closeModal } from '../../reducers/modal/modalActions';
import {
  endSession as endSessionAction,
  updateSession,
} from '../../reducers/session/sessionActions';
import { keepAliveRequest } from '../../backend';

// Selectors
import {
  selectSessionData,
  selectTimeoutModal,
} from '../../reducers/session/sessionSelectors';
import { selectIsAuthenticated } from '../../reducers/auth/authSelectors';
import initialState from '../../store/initialState';

// Components
import WarningModalWrapper from '../common/Modals/WarningModalWrapper/WarningModalWrapper';

// Helpers
import { throttle } from '../../lib/helpers';

const SessionManager = ({ children }) => {
  const dispatch = useDispatch();
  const sessionData = useSelector(selectSessionData);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const timeoutModal =
    useSelector(selectTimeoutModal) || initialState.session.timeoutModal;

  const sessionTimeout = 780000;

  const [userActionFlag, setUserActionFlag] = useState(false);
  const [latestAccessTime, setLatestAccessTime] = useState(null);
  const [warningDuration, setWarningDuration] = useState(
    Duration.fromObject({ minutes: timeoutModal.duration })
  );
  const [duration, setDuration] = useState(timeoutModal.duration);
  const [meaningfulTime, setMeaningfulTime] = useState(sessionTimeout);

  const meaningfulTimeoutRef = useRef(null);
  const warningCountdownRef = useRef(null);
  const warningTimerRef = useRef(null);
  const sessionTimerRef = useRef(null);

  const setupTimer = (ref, callback, delay) => {
    // console.log('setupTimer() called');

    clearTimeout(ref.current);
    ref.current = setTimeout(callback, delay);
  };

  // Scenario of page refresh or component mount, taken from old componentDidMount()
  useEffect(() => {
    const { maxIdleExpirationTime } = sessionData;

    setupTimer(
      sessionTimerRef,
      hasUserTakenAction,
      intervalUntilExpiration(maxIdleExpirationTime)
    );

    initializeMeaningfulTimeout();

    if (Date.parse(maxIdleExpirationTime) - Date.parse(new Date()) < 0) {
      keepAlive();
    }

    return () => {
      clearTimeout(meaningfulTimeoutRef.current);
      clearTimeout(sessionTimerRef.current);
      removeEventListeners();
    };
  }, []);

  // Scenario of changes in session data, taken from old UNSAFE_componentWillReceiveProps()
  useEffect(() => {
    if (!sessionData || !sessionData.latestAccessTime) {
      return;
    }

    const { maxIdleExpirationTime } = sessionData;

    setWarningDuration(Duration.fromObject({ minutes: timeoutModal.duration }));
    setDuration(timeoutModal.duration);
    setMeaningfulTime(sessionTimeout);

    if (
      sessionData.latestAccessTime !== latestAccessTime &&
      isRecent(sessionData.latestAccessTime)
    ) {
      setupTimer(
        sessionTimerRef,
        hasUserTakenAction,
        intervalUntilExpiration(maxIdleExpirationTime)
      );
    }

    return () => {
      clearTimeout(sessionTimerRef.current);
    };
  }, [sessionData]);

  useEffect(() => {
    clearTimeout(meaningfulTimeoutRef.current);

    if (timeoutModal && timeoutModal.name) {
      initializeMeaningfulTimeout();
    };

    return () => {
      clearTimeout(meaningfulTimeoutRef.current);
      dispatch(closeModal());
    }
  }, [timeoutModal]);

  const meaningfulActionTriggered = throttle(event => {
    if (!isMeaningfulEvent(event) || !isAuthenticated) {
      return;
    }

    removeEventListeners();
    setUserActionFlag(true);
    initializeMeaningfulTimeout();
  }, 200);

  const initializeCountdown = () => {
    setWarningDuration(Duration.fromObject({ minutes: duration }));

    setupTimer(warningCountdownRef, decrementWarningDuration, 1000);
    setupTimer(warningTimerRef, endSession, duration * 60 * 1000);
  };

  const decrementWarningDuration = () => {
    setWarningDuration(prevDuration => prevDuration.minus({ second: 1 }));
    setupTimer(warningCountdownRef, decrementWarningDuration, 1000);
  };

  const stopCountdown = () => {
    clearInterval(warningCountdownRef.current);
    clearTimeout(warningTimerRef.current);
    clearTimeout(meaningfulTimeoutRef.current);
  };

  const initializeMeaningfulTimeout = () => {
    addEventListeners();

    setupTimer(meaningfulTimeoutRef, beginSessionWarning, sessionTimeout);
  };

  const beginSessionWarning = () => {
    removeEventListeners();
    dispatch(openModal(timeoutModal.name));
  };

  const hasUserTakenAction = () => {
    removeEventListeners();
    if (userActionFlag) {
      keepAlive();
    }
  };

  const userClickModal = () => {
    keepAlive();
    dispatch(closeModal(timeoutModal.name));
  };

  const beginMeaningfulWatch = () => {
    removeEventListeners();
    initializeMeaningfulTimeout();
  };

  const addEventListeners = () => {
    window.addEventListener('scroll', meaningfulActionTriggered, true);
    window.addEventListener('click', meaningfulActionTriggered, true);
    window.addEventListener('focusin', meaningfulActionTriggered, true);
  };

  const removeEventListeners = () => {
    window.removeEventListener('scroll', meaningfulActionTriggered, true);
    window.removeEventListener('click', meaningfulActionTriggered, true);
    window.removeEventListener('focusin', meaningfulActionTriggered, true);
  };

  const isRecent = ISO => {
    return (
      DateTime.local().toMillis() - DateTime.fromISO(ISO).toMillis() <
      meaningfulTime
    );
  };

  const isMeaningfulEvent = event => {
    if (!event) return false;
    return (
      event.type === 'scroll' ||
      event.type === 'click' ||
      event.type === 'focusin' ||
      (event.type === 'click' &&
        (event.target.nodeName === 'BUTTON' ||
          event.target.nodeName === 'A')) ||
      (event.type === 'focusin' && event.target.nodeName === 'INPUT')
    );
  };

  const keepAlive = () => {
    dispatch(
      keepAliveRequest(
        process.env.REACT_APP_FORGEROCK_URL + '/refreshSessionToken'
      )
    )
      .then(() => {
        const expiresIn = new Date(
          new Date().getTime() + 15 * 60000
        ).toISOString();
        dispatch(updateSession(expiresIn));
        setUserActionFlag(false);
        beginMeaningfulWatch();
        setupTimer(
          sessionTimerRef,
          hasUserTakenAction,
          intervalUntilExpiration(expiresIn)
        );
      })
      .catch(() => {
        endSession();
      });
  };

  const endSession = () => {
    removeEventListeners();
    clearTimeout(meaningfulTimeoutRef.current);
    clearInterval(warningCountdownRef.current);
    clearTimeout(warningTimerRef.current);
    clearTimeout(sessionTimerRef.current);
    dispatch(endSessionAction(timeoutModal.warning));
  };

  const intervalUntilExpiration = expires => {
    return (
      DateTime.fromISO(expires)
        .minus({ seconds: 30 })
        .toMillis() - DateTime.local().toMillis()
    );
  };

  return (
    <>
      {children}
      <WarningModalWrapper
        name={timeoutModal.name}
        duration={warningDuration}
        onOpen={initializeCountdown}
        onClose={stopCountdown}
        keepAlive={userClickModal}
      />
    </>
  );
};

SessionManager.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SessionManager;
